import type { FC, MouseEvent } from 'react';
import Link from 'src/components-v2/Inputs/Link/Link';
import { useTrack } from 'src/contexts';
import { useCriticalRenderContext } from 'src/hooks/useCriticalRenderContext';
import { Events } from 'src/lib/trackingEvents';
import Box from 'threads5/Box';
import styled from 'threads5/styles/styled';

// Custom selector for Organic Marketing <> Google Tag Manager
const LINK_ID_FOR_GTM = 'gtm-homepage-hero-cta';

const LinkAnchor = styled('a')<{ haveAllCriticalAssetsLoaded: boolean }>(
  ({ theme, haveAllCriticalAssetsLoaded }) => {
    return {
      position: 'relative',
      zIndex: 2,
      marginTop: '32px',
      display: 'flex',
      alignItems: 'center',
      background: theme.palette['base/sky/200'],
      boxShadow: '0 1px 16.5px 0 rgba(0, 0, 0, 0.14)',
      height: '84px',
      width: '100%',
      borderRadius: '2.4rem',
      overflow: 'hidden',
      transition:
        'box-shadow .25s ease, background-color .25s ease, scale .25s ease, opacity .5s ease',
      opacity: Number(haveAllCriticalAssetsLoaded),
      [theme.breakpoints.up('sm')]: {
        maxWidth: '520px',
        marginTop: '28px',
      },
      [theme.breakpoints.up('md')]: {
        marginTop: '32px',
      },

      [theme.breakpoints.up('lg')]: {
        marginTop: '42px',
      },
      ':hover, :focus-visible': {
        boxShadow: '0 1px 16.5px 0 rgba(0, 0, 0, 0.40)',
        backgroundColor: theme.palette['base/sky/100'],
        '.arrow-btn-icon': {
          width: '100%',
        },
        '.image': {
          scale: '1.05',
        },
      },
      ':active': {
        scale: '.98',
        background: theme.palette['base/sky/100'],
        boxShadow: '0 1px 16.5px 0 rgba(0, 0, 0, 0.14)',
        '.image': {
          scale: 'none',
        },
      },
    };
  },
);

const ImageContainer = styled('span')(({ theme }) => {
  return {
    height: '100%',
    flexShrink: 0,
    width: '104px',
    [theme.breakpoints.up('sm')]: {
      width: '130px',
    },
    overflow: 'hidden',
  };
});

const Image = styled('img')({
  objectFit: 'cover',
  width: '100%',
  height: '100%',
  transition: 'scale .25s ease',
});

const Content = styled(Box)(({ theme }) => {
  return {
    color: theme.palette['base/sky/1000'],
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: '0 18px',
    [theme.breakpoints.up('sm')]: {
      padding: '0 28px',
    },
    fontFamily: theme.typography.cern,
  };
});

const Heading = styled('h2')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    fontSize: '1.6rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '2rem',
    },
    fontWeight: 600,
    lineHeight: '110%',
    letterSpacing: '-0.04rem',
    margin: 0,
    textWrap: 'balance',
    color: '#000',
  };
});

const ArrowBtn = styled('span')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.4rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.8rem',
    },
    lineHeight: '104%',
    letterSpacing: '-0.036rem',
    gap: theme.spacing(0.75),
  };
});

const ArrowBtnContainer = styled('span')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '11px',
    height: '11px',
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
      width: '13px',
      height: '13px',
    },
  };
});

const ArrowBtnIcon = styled('span')(({ theme }) => {
  return {
    width: 0,
    height: '1px',
    position: 'relative',
    background: theme.palette['base/sky/1000'],
    transition: 'width .25s ease',
    marginTop: '1.5px',
    '::before, ::after': {
      content: '""',
      display: 'block',
      width: '6px',
      [theme.breakpoints.up('sm')]: {
        width: '7px',
      },
      height: '1px',
      background: theme.palette['base/sky/1000'],
      position: 'absolute',
      right: 0,
    },
    '::before': {
      transform: 'rotate(45deg)',
      top: '0px',
      transformOrigin: '100% 100%',
    },
    '::after': {
      transform: 'rotate(-45deg)',
      bottom: '0px',
      transformOrigin: '100% 0%',
    },
  };
});

const HeroCta: FC = () => {
  const { isPlaidSansLoaded } = useCriticalRenderContext();
  const track = useTrack();

  const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    track({
      type: Events.BUTTON_CLICKED,
      payload: {
        event,
        trackingId: Events.BUTTON_CLICKED,
        currentTarget: event.currentTarget,
        buttonName: 'homepage.hero.cta',
        buttonHref: event.currentTarget.href,
      },
    });
  };

  return (
    <Link href='/whats-new/fall-2026/'>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- We are correctly merging `onClick` with the parent `href`; ESLint just doesn't know about it */}
      <LinkAnchor
        onClick={handleCtaClick}
        id={LINK_ID_FOR_GTM}
        haveAllCriticalAssetsLoaded={isPlaidSansLoaded}
      >
        <ImageContainer>
          <picture>
            <source
              srcSet={[
                'https://images.ctfassets.net/ss5kfr270og3/7jQL5lfM9zfLSLlhmuSx6Q/c43f8a5ef25d73342337a8631e73b80d/fr-hero-image.png?w=130&q=70&fm=webp',
                'https://images.ctfassets.net/ss5kfr270og3/7jQL5lfM9zfLSLlhmuSx6Q/c43f8a5ef25d73342337a8631e73b80d/fr-hero-image.png?w=260&q=70&fm=webp 2x',
              ].join(', ')}
              media='(width > 639px)'
            />
            <source
              srcSet={[
                'https://images.ctfassets.net/ss5kfr270og3/28g8zSoWP0auIqRzzCL0oH/af1dce7f2199fe39976626755738e564/fr-hero-image-mobile.png?w=104&q=70&fm=webp',
                'https://images.ctfassets.net/ss5kfr270og3/28g8zSoWP0auIqRzzCL0oH/af1dce7f2199fe39976626755738e564/fr-hero-image-mobile.png?w=208&q=70&fm=webp 2x',
              ].join(', ')}
              media='(width <= 639px)'
            />
            <Image
              src='https://images.ctfassets.net/ss5kfr270og3/7jQL5lfM9zfLSLlhmuSx6Q/c43f8a5ef25d73342337a8631e73b80d/fr-hero-image.png?w=260&q=70&fm=webp'
              alt='Fall Product Release 2026'
              width={260}
              height={168}
              className='image'
            />
          </picture>
        </ImageContainer>
        <Content>
          <Heading>New products just dropped 👀</Heading>
          <ArrowBtn>
            See what's new across Plaid
            <ArrowBtnContainer>
              <ArrowBtnIcon className='arrow-btn-icon' />
            </ArrowBtnContainer>
          </ArrowBtn>
        </Content>
      </LinkAnchor>
    </Link>
  );
};

export default HeroCta;
